import { Box, FormControl, InputLabel, Typography, useTheme } from '@mui/material';
import { usePasswordChange } from '_api';
import { PatientRouteMap } from '_constants';
import { SEVERITY } from '_constants/derived';
import { PrimaryButton, StyledOutlinedInput } from 'components/atoms';
import { AlertMessage } from 'components/molecules';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useUser } from 'utils/hooks';

import { LoginPaper } from './styles';

export const ChangePassword = () => {
  const theme = useTheme();
  const { account } = useUser();
  const navigate = useNavigateTo();
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: passwordChange } = usePasswordChange();

  useEffect(() => {
    if (account && account.status === 'ACTIVE') navigate(PatientRouteMap.TABLE);
  }, [account]);

  const handleNewPasswordChange = ({
    target: { value: newValue },
  }: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(newValue);
  };

  const handleNewPasswordConfirmationChange = ({
    target: { value: newValue },
  }: ChangeEvent<HTMLInputElement>) => {
    setNewPasswordConfirmation(newValue);
  };

  const confirmChange = () => {
    if (newPassword === newPasswordConfirmation) {
      passwordChange({ newPassword, newPasswordConfirmation })
        .then(() => {
          setShowSuccess(true);
          setTimeout(() => {
            navigate(PatientRouteMap.TABLE);
          }, 1500);
        })
        .catch(async (error) => {
          if (error.response) {
            const responseBody = await error.response.json();
            setErrorMessage(responseBody.message || 'Invalid request');
          } else {
            setErrorMessage(
              'An unexpected error occured. Please try again. If this issue persists, try refreshing the page.',
            );
          }
        });
    } else {
      setErrorMessage('Passwords provided do not match. Please try again.');
    }
  };

  return (
    <LoginPaper>
      <img src="/img/logo-sign-in.png" alt={''} style={{ maxWidth: '20%' }} />
      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
        For security purposes, please update your password.
      </Typography>
      <Box style={{ alignSelf: 'start', marginBottom: '10px' }}>
        <Typography variant="body1">
          Hello {account?.firstName} {account?.lastName},
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          New Password Requirements:
        </Typography>
        <Typography variant="body2">- At least 8 characters long</Typography>
        <Typography variant="body2">
          - Includes a combination of uppercase and lowercase letters, and numbers
        </Typography>
        <Typography variant="body2" sx={{ marginTop: 1 }}>
          If you have any issues, please contact support.
        </Typography>
      </Box>
      <FormControl required={true} style={{ width: '100%', marginBottom: '10px' }}>
        <StyledOutlinedInput type="password" onChange={handleNewPasswordChange} />
        <InputLabel sx={{ background: theme.palette.background.paper }}>
          New Password
        </InputLabel>
      </FormControl>
      <FormControl required={true} style={{ width: '100%', marginBottom: '10px' }}>
        <StyledOutlinedInput
          type="password"
          onChange={handleNewPasswordConfirmationChange}
        />
        <InputLabel sx={{ background: theme.palette.background.paper }}>
          Confirm Password
        </InputLabel>
      </FormControl>
      <PrimaryButton aria-label="close" onClick={confirmChange} sx={{ width: '100%' }}>
        Confirm Password Change
      </PrimaryButton>
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Password Successfully updated'}
      />
    </LoginPaper>
  );
};
